/**
 * Default trade journal templates.
 * Mechanics (entry/exit/size/P&L) are captured automatically — prompts focus on decisions.
 */

export const MINIMAL_TEMPLATE = {
  name: 'Minimal',
  description: 'Quick and simple — just the essentials after a trade',
  tags: ['Quick', 'Essentials'],
  icon: 'lightning',
  accent: '#F59E0B',
  fields: [
    {
      key: 'tradeTime',
      label: 'Trade Time',
      type: 'datetime',
      position: 0,
      autoFill: true
    },
    {
      key: 'ticker',
      label: 'Ticker',
      type: 'text',
      position: 1,
      autoFill: true
    },
    {
      key: 'setup',
      label: 'Setup',
      type: 'single',
      position: 2,
      options: ['Breakout', 'Pullback', 'Reversal', 'Other']
    },
    {
      key: 'tradeQuality',
      label: 'Trade Quality (1-5)',
      type: 'rank',
      position: 3,
      max: 5
    },
    {
      key: 'notes',
      label: 'Notes',
      type: 'text',
      position: 4
    }
  ]
};

export const DEFAULT_DAY_TRADER_TEMPLATE = {
  name: 'Day Trader Default',
  description: 'Setup, reasons, mistakes, and quality — built for day trading review',
  tags: ['Recommended', 'Day trading'],
  icon: 'chart',
  accent: '#3B82F6',
  fields: [
    {
      key: 'tradeTime',
      label: 'Trade Time',
      type: 'datetime',
      position: 0,
      autoFill: true
    },
    {
      key: 'ticker',
      label: 'Ticker',
      type: 'text',
      position: 1,
      autoFill: true
    },
    {
      key: 'setup',
      label: 'Setup/Strategy',
      type: 'single',
      position: 2,
      options: ['Breakout', 'Pullback', 'Reversal', 'Momentum', 'Scalp', 'Gap Fill', 'Support/Resistance', 'Other']
    },
    {
      key: 'entryReason',
      label: 'Why did you enter?',
      type: 'multi',
      position: 3,
      options: ['Price Action Signal', 'Volume Spike', 'Technical Indicator', 'News/Catalyst', 'Chart Pattern', 'Key Level Break', 'FOMO (Fear of Missing Out)', 'Revenge Trade', 'Other']
    },
    {
      key: 'marketCondition',
      label: 'Market Condition',
      type: 'single',
      position: 4,
      options: ['Trending Up', 'Trending Down', 'Range Bound', 'Volatile/Choppy', 'Low Volatility', 'Opening Range', 'Power Hour']
    },
    {
      key: 'exitReason',
      label: 'Why did you exit?',
      type: 'single',
      position: 5,
      options: ['Target Hit', 'Stop Loss', 'Time Stop', 'Trailing Stop', 'Reversal Signal', 'Risk Management', 'Emotional Exit', 'Other']
    },
    {
      key: 'tradeQuality',
      label: 'Trade Quality (1-5)',
      type: 'rank',
      position: 6,
      max: 5
    },
    {
      key: 'mistakes',
      label: 'Mistakes Made',
      type: 'multi',
      position: 7,
      options: ['FOMO Entry', 'Revenge Trade', 'Overtrading', 'No Clear Plan', 'Ignored Stop Loss', 'Moved Stop Loss', 'Exited Too Early', 'Held Too Long', 'Wrong Position Size', 'No Mistakes']
    },
    {
      key: 'rMultiple',
      label: 'R Multiple',
      type: 'number',
      position: 8,
      placeholder: 'e.g., 2.5 (2.5x risk)'
    },
    {
      key: 'notes',
      label: 'Notes',
      type: 'text',
      position: 9,
      placeholder: 'Additional thoughts or observations...'
    }
  ]
};

/**
 * From: Trading Journal Prompts That Actually Expose Your Mistakes
 * https://www.precisiontrader.tech/blog/trading-journal-prompts
 * Focuses on rule adherence and emotional triggers — not trade mechanics.
 */
export const MISTAKE_EXPOSER_TEMPLATE = {
  name: 'Mistake Detector',
  description: 'The prompts that expose why you break your own rules',
  tags: ['Process', 'Psychology'],
  icon: 'search',
  accent: '#F43F5E',
  fields: [
    {
      key: 'whyTaken',
      label: 'Why did I take this trade?',
      type: 'single',
      position: 0,
      options: [
        'Clear setup / plan',
        'Bored / restless',
        'FOMO',
        'Revenge after a loss',
        'Other'
      ]
    },
    {
      key: 'maxLossPlanned',
      label: 'What was my max loss (dollars) before entry?',
      type: 'currency',
      position: 1,
      placeholder: 'A number — not a percentage'
    },
    {
      key: 'followedRules',
      label: 'Did I follow my own rules on this trade?',
      type: 'single',
      position: 2,
      options: ['Yes', 'No']
    },
    {
      key: 'ruleBroken',
      label: 'If no, which rule did I break and when?',
      type: 'text',
      position: 3,
      placeholder: 'Be specific — e.g. moved stop after first rejection'
    },
    {
      key: 'feelingBeforeBreak',
      label: 'What was I feeling right before I broke the rule?',
      type: 'multi',
      position: 4,
      options: [
        'Anxious',
        'Frustrated',
        'Bored',
        'FOMO',
        'Overconfident',
        'Revenge',
        'Calm',
        'N/A — followed rules'
      ]
    },
    {
      key: 'rewindAdvice',
      label: 'If I could rewind 30 seconds, what would I tell myself?',
      type: 'text',
      position: 5,
      placeholder: 'The sentence that would have stopped you'
    },
    {
      key: 'takeAgain',
      label: 'Would I take this exact trade again with the same information?',
      type: 'single',
      position: 6,
      options: [
        'Yes — process was right',
        'No — I chased or broke rules',
        'Unsure'
      ]
    }
  ]
};

export const DEFAULT_TEMPLATES = [
  MISTAKE_EXPOSER_TEMPLATE,
  MINIMAL_TEMPLATE,
  DEFAULT_DAY_TRADER_TEMPLATE,
];
